import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AdminPageShell } from "@/components/shared/admin-page-shell";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/shared/loader";
import { NotificationDetailSheet } from "@/features/notifications/components/NotificationDetailSheet";
import { NotificationList } from "@/features/notifications/components/NotificationList";
import { PreferencesForm } from "@/features/notifications/components/PreferencesForm";
import {
  useMarkAllNotificationsRead,
  useNotificationInbox,
  useUnreadNotificationCount,
} from "@/features/notifications/hooks/useNotifications";
import type { UserNotification } from "@/types/notification";

const PAGE_SIZE = 25;

export default function NotificationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState<UserNotification | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const page = Math.max(0, Number(searchParams.get("page") ?? "1") - 1);
  const selectedId = searchParams.get("id");

  const { data, isLoading, error } = useNotificationInbox({
    page: page + 1,
    pageSize: PAGE_SIZE,
  });
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const markAllRead = useMarkAllNotificationsRead();

  const items = data?.items ?? [];
  const pageCount = Math.max(1, data?.meta.totalPages ?? 1);

  const patchParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value) next.set(key, value);
            else next.delete(key);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (page > 0 && page >= pageCount) {
      patchParams({ page: String(pageCount) });
    }
  }, [page, pageCount, patchParams]);

  useEffect(() => {
    if (selectedId) setDetailOpen(true);
  }, [selectedId]);

  function handleSelect(notification: UserNotification) {
    setSelected(notification);
    setDetailOpen(true);
    patchParams({ id: notification.id });
  }

  function handleDetailOpenChange(open: boolean) {
    setDetailOpen(open);
    if (!open) {
      setSelected(null);
      patchParams({ id: undefined });
    }
  }

  return (
    <AdminPageShell
      title="الإشعارات"
      description="صندوق الوارد وتفضيلات إشعارات البريد الإلكتروني لأحداث سير عمل المحتوى"
      isLoading={isLoading && !data}
      error={error}
      actions={
        unreadCount > 0 ? (
          <Button
            variant="outline"
            onClick={() => void markAllRead.mutateAsync()}
            disabled={markAllRead.isPending}
          >
            تعليم الكل كمقروء
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-8">
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold">صندوق الوارد</h2>
          </div>

          {isLoading && !data ? (
            <div className="flex justify-center py-8">
              <Loader />
            </div>
          ) : (
            <>
              <NotificationList items={items} onSelect={handleSelect} />

              {pageCount > 1 && (
                <div className="flex items-center justify-between gap-3 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 0}
                    onClick={() => patchParams({ page: String(page) })}
                  >
                    السابق
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    صفحة {page + 1} من {pageCount}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page + 1 >= pageCount}
                    onClick={() => patchParams({ page: String(page + 2) })}
                  >
                    التالي
                  </Button>
                </div>
              )}
            </>
          )}
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold">تفضيلات البريد</h2>
          <PreferencesForm />
        </section>
      </div>

      <NotificationDetailSheet
        notificationId={selected?.id ?? selectedId}
        open={detailOpen}
        onOpenChange={handleDetailOpenChange}
        preview={selected ?? undefined}
      />
    </AdminPageShell>
  );
}
